import classNames from 'classnames';
import { hasNonEmptyValue } from 'Lib/Any';
import { union, without } from 'ramda';
import React, { useState } from 'react';
import { Checkbox, Field } from '..';
import { withInjectedProps } from '../InjectedPropsContext';
import { ChangeEventWithData } from '../types';
import './CheckboxTree.scss';
import { CheckboxTreeOption as Option, CheckboxTreeOptionGroup as OptionGroup, CheckboxTreeProps } from './types';

/**
 * Type-checker to differentiate option groups with option leafs
 */
const isOptionGroup = (option: OptionGroup | Option): option is OptionGroup =>
  (option as OptionGroup)?.options !== undefined;

/**
 * Returns an iterator that goes over all options within a group
 */
function* generateChildOptions(group: OptionGroup): Generator<Option, void, void> {
  for (let o of group.options) {
    if (isOptionGroup(o)) {
      yield* generateChildOptions(o);
    } else {
      yield o;
    }
  }
}

/**
 * Returns the names (identifiers) for all options within a group
 */
const getChildOptionNames = (group: OptionGroup) => [...generateChildOptions(group)].map((o) => o.name);

/**
 * Size of indent for checkboxes under a group (in pixels)
 */
const checkboxIndent = 20;

/**
 * A generic component for allowing users to select from a set of options, displayed in a checkbox tree.
 *
 * This is intended as a specialised UI component, not a component to be embedded within a Field on a worksheet.
 *
 * Can be controlled (selectedOptions are provided by props) or uncontrolled (tracked internally only)
 *
 * Currently the "value" of this component is an array of the names of the options that have been selected within it.
 * This does mean the whole component re-renders on any selection change - we'll probably have to revise this if we
 * want to make a more complicated selection component.
 *
 * Still a work in progress.
 */
let CheckboxTree: React.FC<CheckboxTreeProps> = ({
  options,
  selectedOptions: selectedOptionsFromProps,
  onSelectionChange,
}) => {
  const isControlled = selectedOptionsFromProps !== undefined;

  const [internalSelectedOptions, setInternalSelectedOptions] = useState<Array<Option['name']>>([]);

  const selectedOptions = isControlled ? (selectedOptionsFromProps as Array<Option['name']>) : internalSelectedOptions;

  const setOptionSelected = (option: Option, isSelected: boolean) =>
    setOptionsSelectedByName([option.name], isSelected);

  const setOptionGroupSelected = (optionGroup: OptionGroup, isSelected: boolean) =>
    setOptionsSelectedByName(getChildOptionNames(optionGroup), isSelected);

  const setOptionsSelectedByName = (optionNames: Array<string>, isSelected: boolean) => {
    const newSelectedOptions = isSelected ? union(selectedOptions, optionNames) : without(optionNames, selectedOptions);

    if (!isControlled) setInternalSelectedOptions(newSelectedOptions);
    onSelectionChange?.(newSelectedOptions);
  };

  function* generateOptionCheckboxes(
    options: Array<OptionGroup | Option>,
    depth: number = 0,
    parentKey: string = ''
  ): Generator<JSX.Element, void, void> {
    let index = 0;
    for (let o of options) {
      let key = `${hasNonEmptyValue(parentKey) ? `${parentKey}.` : ''}${index++}`;
      if (isOptionGroup(o)) {
        yield (
          <div
            className={classNames('CheckboxTree-Selection, CheckboxTree-Selection-group')}
            style={{ paddingLeft: checkboxIndent * depth }}
            key={key}
          >
            <Field
              input={<Checkbox />}
              name={o.name}
              label={o.label}
              value={getChildOptionNames(o).every((o) => selectedOptions.includes(o))}
              onChange={(e: ChangeEventWithData) => setOptionGroupSelected(o as OptionGroup, e.data?.value)}
            />
          </div>
        );
        yield* generateOptionCheckboxes(o.options, depth + 1, key);
      } else {
        yield (
          <div
            className={classNames('CheckboxTree-Selection')}
            style={{ paddingLeft: checkboxIndent * depth }}
            key={key}
          >
            <Field
              input={<Checkbox />}
              name={o.name}
              label={o.label}
              value={selectedOptions.includes(o.name)}
              onChange={(e: ChangeEventWithData) => setOptionSelected(o as Option, e.data?.value)}
            />
          </div>
        );
      }
    }
  }

  return <div className="CheckboxTree">{[...generateOptionCheckboxes(options)]}</div>;
};

CheckboxTree = withInjectedProps(CheckboxTree);

export default CheckboxTree;
export { CheckboxTree };
